import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@mlm/ui';
import { Plus } from 'lucide-react';
import {
  createCourse,
  getCreatorMe,
  listCourseCategories,
  listMyCourses,
  productImageUrl,
} from '../lib/api';

const ACCESS_LABEL: Record<string, string> = {
  free: 'Free',
  purchase: 'Paid',
  subscriber: 'Subscribers',
};

export function CoursesPage() {
  const { data: me } = useQuery({ queryKey: ['creator-me'], queryFn: getCreatorMe });
  // A house account's listings are never reviewed, so the column is noise.
  const isHouse = me?.creator.isHouse === true;
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: courses = [], isLoading } = useQuery({
    queryKey: ['creator-courses'],
    queryFn: listMyCourses,
  });
  const { data: topics = [] } = useQuery({
    queryKey: ['course-categories'],
    queryFn: listCourseCategories,
  });
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<'all' | 'published' | 'drafts'>('all');
  const [form, setForm] = useState({
    name: '',
    price: '',
    categoryId: '',
    accessLevel: 'purchase' as 'free' | 'purchase' | 'subscriber',
  });

  const create = useMutation({
    meta: { successMessage: 'Course created — add your curriculum.' },
    mutationFn: () =>
      createCourse({
        name: form.name,
        price: form.accessLevel === 'purchase' ? form.price : '0',
        accessLevel: form.accessLevel,
        categoryId: form.categoryId || null,
      }),
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: ['creator-courses'] });
      setOpen(false);
      navigate(`/courses/${row.id}`);
    },
  });

  const counts = {
    all: courses.length,
    published: courses.filter((c) => c.status === 'published').length,
    drafts: courses.filter((c) => c.status !== 'published').length,
  };
  const shown = courses.filter((c) =>
    tab === 'all'
      ? true
      : tab === 'published'
        ? c.status === 'published'
        : c.status !== 'published',
  );

  return (
    <div className="max-w-[1040px] space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-lms-heading text-[26px] font-bold tracking-[-0.02em] text-lms-ink sm:text-[30px]">
            Courses
          </h1>
          <div className="mt-1 text-[13.5px] text-lms-muted">
            {counts.all} course{counts.all === 1 ? '' : 's'} · {counts.published} published ·{' '}
            {counts.drafts} draft{counts.drafts === 1 ? '' : 's'}
          </div>
        </div>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="inline-flex h-[42px] items-center gap-2 rounded-full bg-lms px-5 text-sm font-bold text-lms-cream transition-colors hover:bg-lms-dark"
        >
          <Plus className="h-[15px] w-[15px]" strokeWidth={2.4} />
          New course
        </button>
      </div>

      {/* Tabs filter a list that is already in memory — these are a creator's
          own courses, not a catalog, so there is nothing to page. */}
      <div className="flex flex-wrap gap-2">
        {(['all', 'published', 'drafts'] as const).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            aria-pressed={tab === t}
            className={`rounded-full border px-4 py-[7px] text-[13.5px] font-semibold capitalize transition-colors ${
              tab === t
                ? 'border-lms bg-lms-light text-lms'
                : 'border-lms-hairline bg-white text-lms-ink/80 hover:border-lms'
            }`}
          >
            {t === 'all' ? 'All' : t} ({counts[t]})
          </button>
        ))}
      </div>

      {isLoading ? (
        <p className="py-10 text-center text-sm text-lms-muted">Loading…</p>
      ) : shown.length === 0 ? (
        <p className="py-10 text-center text-sm text-lms-muted">
          {courses.length === 0
            ? 'No courses yet. Create one to start building your curriculum.'
            : 'Nothing in this tab.'}
        </p>
      ) : (
        <div className="grid gap-[18px] [grid-template-columns:repeat(auto-fill,minmax(min(100%,300px),1fr))]">
          {shown.map((c) => {
            const published = c.status === 'published';
            const flag =
              c.contentReviewStatus === 'pending'
                ? { text: 'Content pending', title: undefined as string | undefined }
                : c.contentReviewStatus === 'rejected'
                  ? { text: 'Content rejected', title: c.contentReviewReason ?? undefined }
                  : null;
            return (
              <Link
                key={c.id}
                to={`/courses/${c.id}`}
                className="block overflow-hidden rounded-2xl border border-lms-hairline bg-white transition-colors hover:border-lms"
              >
                <div className="relative">
                  <div className="aspect-[16/8] w-full bg-lms-light">
                    {c.images.length > 0 && (
                      <img
                        src={productImageUrl(c.productId, 0)}
                        alt=""
                        loading="lazy"
                        className="h-full w-full object-cover"
                      />
                    )}
                  </div>
                  <span
                    className={`absolute left-3 top-3 rounded-full px-3 py-1 text-[11.5px] font-bold ${
                      published ? 'bg-lms text-lms-cream' : 'bg-lms-gold-tint text-lms-gold-deep'
                    }`}
                  >
                    {published ? 'Published' : 'Draft'}
                  </span>
                  {flag && (
                    <span
                      title={flag.title}
                      className="absolute right-3 top-3 rounded-full bg-lms-gold-tint px-3 py-1 text-[11.5px] font-bold text-lms-gold-deep"
                    >
                      {flag.text}
                    </span>
                  )}
                </div>
                <div className="px-[18px] pb-[18px] pt-4">
                  <div className="font-lms-heading text-[17px] font-bold leading-[1.25] tracking-[-0.01em] text-lms-ink">
                    {c.name}
                  </div>
                  <div className="mt-1 text-[12.5px] text-lms-muted">
                    {ACCESS_LABEL[c.accessLevel] ?? c.accessLevel}
                    {c.accessLevel === 'purchase' ? ` · ₱${c.price}` : ''}
                  </div>
                  <div className="mt-3.5 flex gap-6 border-t border-lms-hairline/70 pt-3">
                    <CardFigure label="Lessons" value={String(c.lessonCount)} />
                    <CardFigure label="Enrolled" value={c.enrollmentCount.toLocaleString()} />
                    {!isHouse && (
                      <CardFigure
                        label="Listing"
                        value={listingLabel(c.approvalStatus)}
                        title={c.rejectionReason ?? undefined}
                        tone={
                          c.approvalStatus === 'approved'
                            ? 'text-lms'
                            : c.approvalStatus === 'rejected'
                              ? 'text-destructive'
                              : 'text-lms-gold-deep'
                        }
                      />
                    )}
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New course</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <label className="text-sm font-medium">Title</label>
              <Input
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Start Your Online Store"
              />
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <label className="text-sm font-medium">Access</label>
                <Select
                  value={form.accessLevel}
                  onValueChange={(v) =>
                    setForm((f) => ({ ...f, accessLevel: v as typeof f.accessLevel }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="purchase">Paid — one-time purchase</SelectItem>
                    <SelectItem value="free">Free — anyone signed in</SelectItem>
                    <SelectItem value="subscriber">Subscribers only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {/* A subscribers-only course has NO price of its own — the
                  server forces it to ₱0 because access comes from the
                  subscription. Showing the field anyway (and requiring it to
                  create) asked the creator for a number we were always going to
                  throw away. */}
              {form.accessLevel === 'subscriber' ? (
                <div className="space-y-1">
                  <label className="text-sm font-medium">Price (₱)</label>
                  <p className="pt-2 text-xs text-muted-foreground">
                    Subscribers-only courses carry no price. Access comes from the subscription
                    plan.
                  </p>
                </div>
              ) : (
                <div className="space-y-1">
                  <label className="text-sm font-medium">Price (₱)</label>
                  <Input
                    value={form.accessLevel === 'free' ? '0' : form.price}
                    disabled={form.accessLevel === 'free'}
                    onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))}
                    inputMode="decimal"
                  />
                </div>
              )}
            </div>
            {topics.length > 0 && (
              <div className="space-y-1">
                <label className="text-sm font-medium">Topic</label>
                <Select
                  value={form.categoryId || 'none'}
                  onValueChange={(v) =>
                    setForm((f) => ({ ...f, categoryId: v === 'none' ? '' : v }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="—" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— No topic —</SelectItem>
                    {topics.map((t) => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              A new course starts as a draft. Its listing is reviewed by the administrator before it
              appears in the catalog.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={() => create.mutate()}
                disabled={
                  !form.name || (form.accessLevel === 'purchase' && !form.price) || create.isPending
                }
              >
                Create
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CardFigure({
  label,
  value,
  tone = 'text-lms-ink',
  title,
}: {
  label: string;
  value: string;
  tone?: string;
  title?: string;
}) {
  return (
    <div title={title}>
      <div className="text-[11px] text-lms-muted">{label}</div>
      <div className={`text-[14.5px] font-bold tabular-nums ${tone}`}>{value}</div>
    </div>
  );
}

function listingLabel(status: string) {
  return status.charAt(0).toUpperCase() + status.slice(1);
}
